<template>
    <div class="plot-layout">
        <!-- Plot layout app-bar -->
        <v-app-bar
            app
            color="white"
            class="pa-0"
            :height="appbarHeight"
        >
            <v-row>
                <!-- Back button -->
                <v-col
                    v-if="backButton"
                    cols="2"
                    md="1"
                >
                    <!-- Return CTA -->
                    <v-btn
                        icon
                        small
                        class="mt-2"
                        @click.prevent="goBack()"
                    >
                        <v-icon class="icon">
                            icon-return
                        </v-icon>
                    </v-btn>
                    <!-- /Return CTA -->
                </v-col>
                <!-- /Back button -->

                <!-- Plot title -->
                <v-col class="d-flex">
                    <v-toolbar-title class="text-center font-weight-medium">
                        {{ $t('utils.name') + ': ' + plotName }}
                    </v-toolbar-title>
                </v-col>
                <!-- /Plot title -->

                <!-- Plot CTAs-->
                <v-col
                    v-if="!isReviewUse"
                    cols="2"
                    class="d-flex justify-end"
                >
                    <!-- Plot information CTA -->
                    <v-btn
                        id="gtm-plotsidebar-cta-plotinfo"
                        ref="plot-information"
                        icon
                        class="popup-cta"
                        :class="visiblePopup['plot-information'] ? 'active' : null"
                        @click.prevent="switchPopup('plot-information')"
                        v-click-outside="{
                            handler: closeLastPopup,
                            closeConditional: isTherePopupOpened,
                            include: isPopupCta
                        }"
                    >
                        <v-icon class="icon">
                            icon-info
                        </v-icon>
                    </v-btn>
                    <!-- /Plot information CTA -->

                    <!-- Metrics CTA -->
                    <v-btn
                        v-if="metrics"
                        id="gtm-plotsidebar-cta-metric"
                        ref="metrics"
                        icon
                        class="popup-cta"
                        :class="visiblePopup['metrics'] ? 'active' : null"
                        @click.prevent="switchPopup('metrics')"
                        v-click-outside="{
                            handler: closeLastPopup,
                            closeConditional: isTherePopupOpened,
                            include: isPopupCta
                        }"
                    >
                        <v-icon class="icon">
                            icon-metrics
                        </v-icon>
                    </v-btn>
                    <!-- /Metrics CTA -->

                    <!-- Share CTA -->
                    <v-btn
                        v-if="(((!metrics && !$vuetify.breakpoint.smAndDown) || data.widgetType === '3d-plan') || $vuetify.breakpoint.smAndUp) && $route.query.key != '5qRvzgfzOHEfe5dYUwGrJY'"
                        id="gtm-plotsidebar-cta-share-plot"
                        ref="share"
                        icon
                        class="popup-cta"
                        :class="visiblePopup['share'] ? 'active' : null"
                        @click.prevent="shareDialog = true"
                        v-click-outside="{
                            handler: closeLastPopup,
                            closeConditional: isTherePopupOpened,
                            include: isPopupCta
                        }"
                    >
                        <v-icon class="icon">
                            icon-share
                        </v-icon>
                    </v-btn>
                    <!-- /Share CTA -->

                    <!-- Contextual menu -->
                    <v-menu
                        v-if="$vuetify.breakpoint.xs && data.widgetType !== '3d-plan'"
                        left
                        bottom
                        ref="contextual-menu"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                icon
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon class="icon">
                                    icon-contextual-menu
                                </v-icon>
                            </v-btn>
                        </template>

                        <v-list class="contextual-menu">
                            <!-- Share item -->
                            <v-list-item
                                v-if="$route.query.key != '5qRvzgfzOHEfe5dYUwGrJY'"
                                id="gtm-plotsidebar-cta-share-plot"
                                v-click-outside="{
                                    handler: closeLastPopup,
                                    closeConditional: isTherePopupOpened,
                                    include: isPopupCta
                                }"
                                @click.prevent="shareDialog = true"
                            >
                                <v-list-item-icon>
                                    <v-icon
                                        class="icon"
                                        color="black"
                                    >
                                        icon-share
                                    </v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title class="text-none">
                                        {{ $t('share.share') }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <!-- /Share item -->
                        </v-list>
                    </v-menu>
                    <!-- /Contextual menu -->
                </v-col>
                <!-- /Plot CTAs-->
            </v-row>
        </v-app-bar>
        <!-- /Plot layout app-bar -->

        <!-- Widgets slide group -->
        <v-container
            v-if="supportedWidgets"
            fluid
            class="widgets-slider-group"
            :style="{ 'top': appbarHeight + 'px' }"
        >
            <v-row>
                <v-col
                    cols="2"
                    md="1"
                >
                    <v-fade-transition>
                        <v-btn
                            v-if="!showWidgets"
                            class="widget-list-cta text-none rounded-pill"
                            medium
                            @click="toggleWidgetList"
                        >
                            {{ $t("utils.displayViews") }}
                            <v-icon class="mdi">
                                mdi-chevron-down
                            </v-icon>
                        </v-btn>
                        <v-btn
                            v-else
                            fab
                            small
                            class="widget-list-cta pulse habiteo-body-2"
                            @click="toggleWidgetList"
                        >
                            <v-icon class="mdi">
                                mdi-chevron-up
                            </v-icon>
                        </v-btn>
                    </v-fade-transition>
                </v-col>
                <v-col 
                    cols="10"
                    md="11"
                    class="widget-list-col pl-0 pr-0 pt-3"
                >
                    <v-slide-x-transition>
                        <v-sheet
                            v-if="showWidgets"
                            class="mx-auto"
                            :max-width="maxScreenWidth"
                        >
                            <v-slide-group
                                center-active
                                :show-arrows="$vuetify.breakpoint.mdAndUp || isIframe"
                            >
                                <v-slide-item
                                    v-for="(item, itemIndex) in supportedWidgets"
                                    :key="`widget-${itemIndex}`"
                                    v-slot="{ active, toggle }"
                                >
                                    <v-btn
                                        v-if="item.submenu"
                                        class="widget-cta mx-2 pa-0"
                                        rounded
                                        medium
                                    >
                                        <v-menu
                                            class="alternate-models"
                                            v-if="item.submenu"
                                            rounded="lg"
                                            offset-y
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn
                                                    class="dropdown-cta text-none"
                                                    rounded
                                                    medium
                                                    :input-value="item.active ? true : active"
                                                    elevelation="0"
                                                    active-class="active"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                >
                                                    <span
                                                        v-if="item.preview"
                                                        class="widget-preview"
                                                    >
                                                        <v-img
                                                            :src="item.preview"
                                                            max-width="35"
                                                            height="35"
                                                            contain
                                                            class="white rounded-circle"
                                                        >
                                                            <template v-slot:placeholder>
                                                                <v-row
                                                                    class="fill-height ma-0"
                                                                    align="center"
                                                                    justify="center"
                                                                >
                                                                    <v-progress-circular
                                                                        indeterminate
                                                                        color="blue lighten-2"
                                                                    />
                                                                </v-row>
                                                            </template>
                                                        </v-img>
                                                    </span>
                                                    <span>
                                                        {{ item.text }}
                                                    </span>
                                                    <v-icon class="mdi">
                                                        mdi-chevron-down
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list class="alternate-models">
                                                <v-list-item
                                                    v-for="(subitem, subindex) in item.submenu"
                                                    :key="`subitem-${subindex}`"
                                                    :class="{'active': item.active && subitem.active }"
                                                    @click.prevent="toggle(); selectItem(item, subitem, itemIndex)"
                                                >
                                                    <v-list-item-avatar v-if="subitem.preview">
                                                        <v-img :src="subitem.preview">
                                                            <template v-slot:placeholder>
                                                                <v-row
                                                                    class="fill-height ma-0"
                                                                    align="center"
                                                                    justify="center"
                                                                >
                                                                    <v-progress-circular
                                                                        indeterminate
                                                                        color="blue lighten-2"
                                                                    />
                                                                </v-row>
                                                            </template>
                                                        </v-img>
                                                    </v-list-item-avatar>
                                                    <v-list-item-content>
                                                        {{ subitem.text }}
                                                    </v-list-item-content>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </v-btn>
                                    <v-btn
                                        v-else
                                        class="mx-2 widget-cta text-none"
                                        :input-value="item.active ? true : active"
                                        active-class="active"
                                        rounded
                                        medium
                                        @click.prevent="toggle();selectItem(item)"
                                    >
                                        <span
                                            v-if="item.preview || item.icon"
                                            class="widget-preview"
                                        >
                                            <v-img
                                                :src="item.preview || item.icon"
                                                max-width="35"
                                                height="35"
                                                contain
                                                rounded
                                                class="white rounded-circle"
                                            >
                                                <template v-slot:placeholder>
                                                    <v-row
                                                        class="fill-height ma-0"
                                                        align="center"
                                                        justify="center"
                                                    >
                                                        <v-progress-circular
                                                            indeterminate
                                                            color="blue lighten-2"
                                                        />
                                                    </v-row>
                                                </template>
                                            </v-img>
                                        </span>
                                        {{ item.text }}
                                    </v-btn>
                                </v-slide-item>
                            </v-slide-group>
                        </v-sheet>
                    </v-slide-x-transition>
                </v-col>
            </v-row>
        </v-container>
        <!-- /Widgets slide group -->

        <v-expand-transition>
            <!-- Plot information popup -->
            <v-card
                v-if="visiblePopup['plot-information']"
                class="alternative-menu-popup pa-4"
                :style="{ top: popupPosition.top + 'px'}"
            >
                <h3 class="text-left">
                    {{ data.details[0].label + " " + data.details[0].value }}
                </h3>
                <v-list class="listreset">
                    <v-subheader class="pa-0 pb-2 text-decoration-underline black--text h3">
                        {{ $t('utils.globalInformation') }}
                    </v-subheader>
                    <v-list-item>
                        <v-card
                            rounded
                            elevation="0"
                            width="100%"
                            class="d-block mb-4 pa-4 price-section text-left"
                        >
                            <span
                                class="global-nav font-weight-bold d-block"
                                v-html="prices.totalPrice.text + ' ' + prices.totalPrice.value"
                            />
                            <span
                                v-if="prices.totalPrice.value !== prices.lotPrice.value"
                                class="d-block habiteo-body-3"
                                v-html="prices.lotPrice.text + ' ' + prices.lotPrice.value"
                            />
                            <span
                                v-if="prices.totalPrice.value !== prices.lotPrice.value"
                                class="d-block habiteo-body-3"
                                v-html="prices.annexPrice.text + ' ' + prices.annexPrice.value"
                            />
                        </v-card>
                    </v-list-item>
                    <v-list-item
                        v-for="(detail, index) in globalDetails"
                        :key="`detail-${index}`"
                        class="pa-0"
                        :class=" { 'plot-description text-left': detail.id === 'description'}"
                        v-show="detail.id != 'name' && detail.value"
                    >
                        {{ detail.label }}
                        <span
                            :style="{ 'color': detail.color }"
                            v-html="detail.id === 'description' ? decodeHTMLfromString(detail.value) : detail.value"
                        />
                    </v-list-item>
                    <v-list-item v-if="tags">
                        <label
                            class="tag"
                            v-for="(tag, tagName) in tags.tags"
                            :key="tagName"
                            :style="{ 'background-color': tag.color }"
                        >
                            {{ tagName }}
                        </label>
                    </v-list-item>
                </v-list>
            </v-card>
            <!-- /Plot information popup -->

            <!-- Metrics popup -->
            <v-card
                v-if="visiblePopup['metrics']"
                class="alternative-menu-popup pa-4"
                :style="{
                    top: popupPosition.top + 'px',
                    minWidth: minWidthPopup + 'px'
                }"
            >
                <div class="metrics">
                    <v-list class="listreset">
                        <v-subheader class="pa-0 pb-2 text-decoration-underline black--text h3">
                            {{ $t('utils.metricInformation') }}
                        </v-subheader>
                        <v-list-item
                            class="pa-0"
                            v-for="(metric, metricName) in metrics.metrics"
                            :key="metric.id + metricName"
                        >
                            {{ metricName }} :<span class="pl-2" v-html="decodeHTMLfromString(metric)"/>
                        </v-list-item>
                    </v-list>
                </div>
            </v-card>
            <!-- /Metrics popup -->

            <!-- Share dialog -->
            <v-dialog
                class="share-favorite-dialog"
                v-model="shareDialog"
                width="320px"
            >
                <v-card>
                    <v-toolbar flat>
                        <h2 class="pl-3 font-weight-regular">
                            {{ $t('share.share') }}
                        </h2>
                        <v-spacer/>
                        <v-toolbar-items>
                            <v-btn
                                text
                                @click="shareDialog = false"
                            >
                                <v-icon>
                                    mdi-close
                                </v-icon>
                            </v-btn>
                        </v-toolbar-items>
                    </v-toolbar>
                    <v-card-text>
                        <div class="d-flex share-favorite-link">
                            <div
                                id="urlToCopy"
                                class="pa-2 text-truncate"
                            >
                                {{ sharedLink }}
                            </div>

                            <v-btn
                                id="gtm-plotsidebar-cta-copy-plot"
                                text
                                class="text-none copy-cta font-weight-bold"
                                @click="copyLink()"
                            >
                                {{ $t('share.copyLink') }}
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>
            </v-dialog>
            <!-- /Share dialog -->

        </v-expand-transition>
    </div>
</template>

<script>
    import { EventBus } from '@/event-bus';
    import { mapActions, mapGetters } from 'vuex';

    export default {
        name: 'PlotLayout',
        props: [
            'data',
            'backButton',
            'goBack'
        ],
        data() {
            return {
                plotName: null,
                selected: [],
                open: [],
                selectWidth: {},
                visiblePopup: {
                    'plot-information': false,
                    'metrics': false,
                    'share': false
                },
                shareDialog: false,
                popupPosition: null,
                displaySelect: true,
                tags: null,
                clickedTracking: false,
                sharedLink: null,
                textToCopy: null,
                metrics: null,
                globalInfo: null,
                appbarHeight: 60,
                minWidthPopup: 260,
                lastPopup: null,
                disabledWidget: [
                    'configurable-tour',
                ],
                showWidgets: true,
                screenWidth: null,
                pricesDetails: [
                    'totalPrice',
                    'lotPrice',
                    'annexPrice'
                ]
            };
        },
        computed: {
            ...mapGetters([
                'isIframe',
                'isLocalstorageAvailable',
                'isReviewUse'
            ]),
            maxScreenWidth: {
                get: function() {
                    return this.screenWidth;
                },
                set: function(value) {
                    this.screenWidth = window.innerWidth;
                }
            },
            isIframe() {
                return window.self !== window.top;
            },
            programUuid() {
                return (this.data && this.data.program) ? this.data.program.id : null;
            },
            plotId() {
                return this.data ? this.data.plotId : null;
            },
            
            supportedWidgets() {
                let widgetList = this.data.items.filter(widget => !this.disabledWidget.includes(widget.id));
                if (widgetList.length === 1)
                    if (widgetList[0].submenu)
                        return widgetList;
                    else
                        return null;
                else
                    return widgetList;
            },
            globalDetails() {
                const details = this.$props.data.details ? this.$props.data.details.filter(detail => !this.pricesDetails.includes(detail.id)) : [];
                return details.length ? details : null;
            },
            prices() {
                const priceList = this.$props.data.details ? this.$props.data.details.filter(detail => this.pricesDetails.includes(detail.id)) : [];
                var prices = {};
                if (priceList.length) {
                    priceList.forEach(price => {
                        prices[price.id] = {
                                'text': price.label,
                                'value': price.value
                            };
                    });
                }
                return prices;
            }
        },
        mounted() {
            let searchPlotName = this.data.details.filter(detail => detail.id === 'name');
            this.plotName = searchPlotName[0].value || null;

            this.getGlobalInfo();
            this.getMetrics();
            const origin = window.location.origin;
            const key = this.$route.query.key;

            if (this.$props.data.plotId)
                this.sharedLink = origin + "/lot?id=" + this.$props.data.plotId + "&key=" + key;
            else {
                const idThreeD = this.$props.data.items.find(item => item.id = "3d-plan");
                this.sharedLink = origin + "/plan-3d?id=" + idThreeD.subid + "&key=" + key;
            }

            this.screenWidth = window.innerWidth;
            window.addEventListener('resize', this.setMaxScreenWidth);
        },
        methods: {
            ...mapActions([
                'setSnackbar'
            ]),
            setMaxScreenWidth() {
                this.screenWidth = window.innerWidth;
            },
            isPopupCta() {
                return [...document.querySelectorAll('.popup-cta')];
            },
            isTherePopupOpened() {
                return this.lastPopup !== null;
            },
            switchPopup(type) {
                if (this.lastPopup)
                    this.closeLastPopup();

                this.visiblePopup[type] = !this.visiblePopup[type];
                if (this.visiblePopup[type]) {
                    this.popupPosition = {
                        top: this.appbarHeight + 10 
                    };
                    this.lastPopup = type;
                }
                this.trackingEventHot();
            },
            closeLastPopup() {
                this.visiblePopup[this.lastPopup] = false;
                this.lastPopup = null;
            },
            selectItem(item, subitem) {
                switch (item.id) {
                    case 'configurable-tour':
                        this.data.items.filter(el => el.active).forEach(el => { el.active = false });
                        this.$set(item, 'active', true);
                        EventBus.$emit('updateLoadedWidgets', {
                            id: 'configurable-tour',
                            link: item.link,
                            active: true,
                            widgetType: 'configurable-tour'
                        });
                        EventBus.$emit('saveSidebar');
                    break;

                    case 'sunshine':
                        this.data.items.filter(el => el.active).forEach(el => { el.active = false });
                        this.$set(item, 'active', true);
                        EventBus.$emit('loadIframeWidget', item.id, item.url);
                    break;

                    default:
                        EventBus.$emit('plot_SetActive', item, subitem);
                    break;
                }
            },
            
            copyLink() {
                let range = document.createRange();
                range.selectNode(document.getElementById("urlToCopy"));
                window.getSelection().removeAllRanges();
                window.getSelection().addRange(range);
                document.execCommand("copy");
                window.getSelection().removeAllRanges();
                this.shareDialog = false;

                this.setSnackbar({
                    'message': this.$t('share.copy'),
                    'orientation': 'top'
                });
            },
            trackingEventHot() {
                let event = "hot";
                let data = this.data.items.filter(el => el.active)[0];
                if (data.id !== "sunshine" && !this.clickedTracking) {
                    this.clickedTracking = true;
                    let trackingData;
                    if (data.submenu) {
                        let submenu = data.submenu.filter(el => el.active)[0];
                        trackingData = submenu.tracking;
                        if (submenu.alternateModel) {
                            trackingData.alternateModel = submenu.alternateModel;
                        }
                    } else {
                        trackingData = data.tracking;
                    }
                    EventBus.$emit('trackingEvent', trackingData, event);
                    setTimeout(() => {
                        this.clickedTracking = false;
                    }, 10000);
                }
            },
            getGlobalInfo(){
                const globalInfo = this.data.details.filter(detail => detail.id !== 'metrics');
                this.globalInfo = globalInfo ? globalInfo : null;
            },
            getMetrics(){
                const metrics = this.data.details.find(detail => detail.id === 'metrics');
                this.metrics = metrics ? metrics : null;
            },
            decodeHTMLfromString(str) {
                let decoder = document.createElement('textarea');
                decoder.innerHTML = str;
                return decoder.value;
            },
            toggleWidgetList() {
                this.showWidgets = !this.showWidgets;
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "@/assets/scss/components/_pulse.scss";
    @import "@/assets/scss/components/_share-favorite.scss";

    .shared-link {
        text-transform: lowercase;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: block;
        line-height: 1.5rem;
    }

    .v-sheet.v-card.price-section {
        background-color: #f3f3f3;
    }
</style>


// WEBPACK FOOTER //
// src/components/navbarLayout/alternativeMenu/PlotLayout.vue